import {
	Component,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output
} from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { FormControl } from '../form-control/form-control.component';
import { FormGroup } from '../form-group/form-group.class';
import { CustomErrorMessage } from '../models/custom-error-message.interface';
import { LABEL_POSITION } from '../enums/label-position.enum';

// We need an Angular decorator now for all Angular components.
// This is an abstract class however and should not be utilized.
// Use inherited input components instead

@Component({
	selector: 'ea-ui-base-input',
	templateUrl: './base-input.component.html',
	styleUrls: ['./base-input.component.scss']
})
export abstract class BaseInputComponent implements OnInit, OnDestroy {
	// Native label/input attributes
	@Input() value: any = '';
	@Input() label: string;
	@Input() name: string;
	@Input() placeholder: string;

	// Validators
	@Input() pattern: RegExp;
	@Input() inputMatch: string;
	@Input() min: number;
	@Input() max: number;
	@Input() minlength: string | number;
	@Input() maxlength: string | number;

	// Label Display and Positioning
	@Input() hideLabel = false;
	@Input() hideOptional = false;
	@Input() labelPosition = LABEL_POSITION.TOP;

	// Form attached to:
	@Input() form: FormGroup;

	// Array of Key / Value pair custom error mappings
	@Input() customErrorMessages: Array<CustomErrorMessage>;

	// eslint-disable-next-line @angular-eslint/no-output-on-prefix
	@Output() readonly onChange = new EventEmitter<any>();
	// eslint-disable-next-line @angular-eslint/no-output-on-prefix
	@Output() readonly onInput = new EventEmitter<any>();

	LABEL_POSITION = LABEL_POSITION;
	control: FormControl;

	private _disabled = false;

	private _required = false;

	@Input() set disabled(isDisabled: boolean) {
		this._disabled = isDisabled;
		if (this.control) {
			this.control[isDisabled ? 'disable' : 'enable']();
		}
	}
	get disabled(): boolean {
		return this._disabled;
	}
	@Input() set required(isRequired: boolean) {
		this._required = isRequired;
		if (this.form?.controls[this.name]) {
			if (this._required) {
				this.form.controls[this.name].setValidators([Validators.required]);
			} else {
				this.form.controls[this.name].clearValidators();
			}
			this.form.get(this.name).updateValueAndValidity();
		}
	}
	get required(): boolean {
		return this._required;
	}

	ngOnInit(): void {
		if (!this.name) {
			console.warn(
				'No Name assigned to input, generating one. Please add "name" parameter to input field.'
			);
			this.name = `input-${Math.floor(Math.random() * 999)}`;
		}
		this.initFormControl();
	}

	initFormControl(): void {
		// Added for the case where we have a component that inherits from base input and contains another input that derives from base input
		// inside its template. We don't want to attach the input twice / get errors to the form, so only attach if we explicitly pass in a form
		if (this.form) {
			this.control = new FormControl(this.value);
			if (this.disabled) {
				this.control.disable();
			}
			this.control.name = this.name;
			this.control.setValidators(this.validators());

			this.control.onReset = this.onReset;
			this.control.onSubmitSuccess = this.onSubmitSuccess;

			this.form.addControl(this.name, this.control);
		}
	}

	onSubmitSuccess = (newValue): void => {
		this.value = newValue;
		this.control.setValue(newValue);
	};

	onReset = (): void => {
		this.control.reset(this.value);
	};

	ngOnDestroy(): void {
		if (this.form) {
			this.form.removeControl(this.name);
		}
	}

	handleChange(): void {
		this.onChange.emit(this.control.value);
	}

	handleInput(): void {
		this.onInput.emit(this.control.value);
	}

	private inputMatchValidator(matchValue: string): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const value = control.value;
			const isValid = value === matchValue;
	
			return value && value.length > 0 && !isValid ? { inputMatch: { value, expected: matchValue } } : null;
		};
	}

	private validators(): Array<ValidatorFn> {
		const validators = [];

		if (this.required) {
			validators.push(Validators.required);
		}
		if (this.pattern) {
			validators.push(Validators.pattern(this.pattern));
		}
		if (this.min) {
			validators.push(Validators.min(this.min));
		}
		if (this.max) {
			validators.push(Validators.max(this.max));
		}
		if (this.inputMatch) {
			validators.push(this.inputMatchValidator(this.inputMatch));
		}

		return validators;
	}
}
